import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Info, Trophy } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ThemeProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  themeName: string;
  totalWords: number;
  currentProgress: number;
}

export const ThemeProgressModal: React.FC<ThemeProgressModalProps> = ({
  isOpen,
  onClose,
  themeName,
  totalWords,
  currentProgress,
}) => {
  if (!isOpen) return null;

  // 5 points per word is the target for 100% completion
  const targetPoints = totalWords * 5;
  const percentage = Math.min(100, Math.floor((currentProgress / targetPoints) * 100)) || 0;
  
  const star1Target = Math.ceil(targetPoints * 0.33);
  const star2Target = Math.ceil(targetPoints * 0.66);
  const star3Target = targetPoints;

  const content = (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:p-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
        >
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col items-center mb-6">
            <div className="flex items-center justify-center w-12 h-12 mb-4 bg-yellow-100 rounded-full">
              <Trophy className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 text-center">
              Theme Progress
            </h3>
            <p className="text-gray-500 font-medium mt-1 text-center">
              {themeName}
            </p>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            {[1, 2, 3].map((star) => {
              const isEarned = percentage >= (star * 33);
              return (
                <motion.div
                  key={star}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: star * 0.1 }}
                >
                  <Star
                    className={`w-12 h-12 transition-all duration-500 ${
                      isEarned 
                        ? 'fill-yellow-400 text-yellow-400 drop-shadow-md' 
                        : 'fill-gray-100 text-gray-300'
                    }`}
                  />
                </motion.div>
              );
            })}
          </div>

          <div className="space-y-6">
            <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-purple-800">
                  Earn points by practicing words! You need an average of <span className="font-bold">5 points per word</span> to achieve 3 stars and fully master this theme.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm font-medium mb-1">
                  <span className="text-gray-700">Total Points Earned</span>
                  <span className="text-purple-600">{currentProgress} / {targetPoints}</span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mt-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">
                  Points Needed
                </h4>
                
                <div className="flex justify-between text-sm items-center">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-gray-600">1 Star</span>
                  </div>
                  <span className={`font-medium ${currentProgress >= star1Target ? 'text-green-500' : 'text-gray-900'}`}>
                    {star1Target} points
                  </span>
                </div>
                
                <div className="flex justify-between text-sm items-center">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-gray-600">2 Stars</span>
                  </div>
                  <span className={`font-medium ${currentProgress >= star2Target ? 'text-green-500' : 'text-gray-900'}`}>
                    {star2Target} points
                  </span>
                </div>
                
                <div className="flex justify-between text-sm items-center">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-gray-600">3 Stars</span>
                  </div>
                  <span className={`font-medium ${currentProgress >= star3Target ? 'text-green-500' : 'text-gray-900'}`}>
                    {star3Target} points
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};
